import React, { useEffect, useMemo, useState } from 'react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography, useMediaQuery, Chip, Stack } from '@mui/material';
import { Search, HighlightOffOutlined } from '@mui/icons-material';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import gaiaTheme from '../../../root-components/theme';
import { SettingsMobileNavbar } from '../../../root-components/navigation/settings-mobile-navbar';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import UserActionLogService from '../../../shared/services/user-action-log/user-action-log.service';
import { IUserActionLog, IUserActionLogFilters } from '../../../shared/services/user-action-log/user-action-log.model';
import UserDetailsService from '../../../shared/services/user-details/user-details.service';
import { IUserDetails } from '../../../shared/services/user-details/user-details.model';
import { capitaliseFirst } from '../../../shared/utils/misc-shared-utils';
import { LoadingPage } from '../loading-page';

export const UserActionLogPage = () => {
  // Services
  const userActionLogService = useMemo(() => new UserActionLogService(), []);
  const userDetailsService = useMemo(() => new UserDetailsService(), []);
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  // State
  const [userActionLogs, setUserActionLogs] = useState<IUserActionLog[]>([]);
  const [users, setUsers] = useState<IUserDetails[]>([]);
  const [filters, setFilters] = useState<IUserActionLogFilters>({
    propertyName: '',
    oldValue: '',
    newValue: '',
    actionDescription: '',
    userId: '',
    organisationId: '',
    referenceModelName: '',
    referenceObjectId: '',
    startDate: '',
    endDate: ''
  });
  const [appliedFilters, setAppliedFilters] = useState<IUserActionLogFilters>({
    propertyName: '',
    oldValue: '',
    newValue: '',
    actionDescription: '',
    userId: '',
    organisationId: '',
    referenceModelName: '',
    referenceObjectId: '',
    startDate: '',
    endDate: ''
  });
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));

  useEffect(() => {
    getAllUserActionLogs()
  }, [inputPage, perPage, change])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [appliedFilters, order, orderBy])

  useEffect(() => {
    getUsers();
  }, [userDetailsService]);

  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      const start = moment(filters.startDate, 'YYYY-MM-DD');
      const end = moment(filters.endDate, 'YYYY-MM-DD');
      if (end.isBefore(start, 'day')) {
        setFilters(prev => ({ ...prev, endDate: filters.startDate }));
      }
    }
  }, [filters.startDate, filters.endDate]);


  const buildFilterQueryString = (filterValues: IUserActionLogFilters) => {
    const params = Object.entries(filterValues)
      .filter(([key, value]) => key !== 'page' && key !== 'perPage' && value !== undefined && value !== null && value !== '')
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    return params.length ? `&${params.join('&')}` : '';
  };

  const getAllUserActionLogs = () => {
    const queryString = buildFilterQueryString(appliedFilters);
    userActionLogService.getAllWithPagination(inputPage, perPage, orderBy, order, queryString)
      .then(response => {
        setIsLoading(false)
        setTotalRecords(response.count);
        setUserActionLogs(response.rows)
      })
      .catch(err => {
        setIsLoading(false)
        console.log(err)
      })
  }

  const getUsers = async () => {
    try {
      const usersData = await userDetailsService.getAllUserDetails('?status=active');
      setUsers(usersData);
    } catch (error) {
      console.error('Error getting users:', error);
    }
  };

  // Table configuration
  const headCells: IHeadCell[] = [
    { id: 'createdAt', label: 'Date', numeric: false, sortable: false },
    { id: 'actionDescription', label: 'Action', numeric: false, sortable: false },
    { id: 'user', label: 'User', numeric: false, sortable: false },
    // { id: 'organisation', label: 'Organisation', numeric: false, sortable: false },
    { id: 'referenceModelName', label: 'Model', numeric: false, sortable: false },
    { id: 'referenceObjectId', label: 'Instance ID', numeric: false, sortable: false },

    { id: 'propertyName', label: 'Property', numeric: false, sortable: false },
    { id: 'oldValue', label: 'From', numeric: false, sortable: false },
    { id: 'newValue', label: 'To', numeric: false, sortable: false },
    // { id: 'referenceObjectId', label: 'Object ID', numeric: false, sortable: false }
  ];

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    const emptyFilters = {
      propertyName: '',
      oldValue: '',
      newValue: '',
      actionDescription: '',
      userId: '',
      organisationId: '',
      referenceModelName: '',
      referenceObjectId: '',
      startDate: '',
      endDate: ''
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  const applyFilters = () => {
    setAppliedFilters(filters);
  };

  const rows = !userActionLogs.length ? [] : userActionLogs.map(log => ({
    id: log.id,
    createdAt: moment(log.createdAt).format('DD/MM/YYYY HH:mm'),
    user: `${log.user.firstName} ${log.user.surname}`,
    // organisation: log.organisation.name,
    propertyName: log.propertyName,
    referenceObjectId: log.referenceObjectId,
    referenceModelName: log.referenceModelName,

    oldValue: log.oldValue,
    newValue: log.newValue,
    // referenceObjectId: log.referenceObjectId,
    actionDescription: (
      <Chip
        label={capitaliseFirst(log.actionDescription)}
        color={
          log.actionDescription === 'CREATED' ? 'success' :
            log.actionDescription === 'UPDATED' ? 'warning' :
              log.actionDescription === 'DELETED' ? 'error' : 'default'
        }
        size="small"
        variant='outlined'
      />
    )
  }));

  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  return (
    <PageOuterContainer>
      {isMobileMode && <Box sx={{ pb: 4 }}>
        <SettingsMobileNavbar />
      </Box>
      }
      <PageHeading
        title="User Action Log"
      />
      <Stack mb={2}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Filters
          </Typography>
          <Stack direction="row" gap={1}>
            <Button
              variant="contained"
              startIcon={<Search />}
              onClick={applyFilters}
              disabled={isLoading}
            >
              <Typography>Search</Typography>
            </Button>
            <Button
              variant="outlined"
              startIcon={<HighlightOffOutlined />}
              onClick={clearFilters}
              disabled={isLoading}
            >
              <Typography>Clear</Typography>
            </Button>
          </Stack>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Start Date"
                value={filters.startDate ? moment(filters.startDate) : null}
                onChange={(date) => handleFilterChange('startDate', date ? date.format('YYYY-MM-DD') : '')}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                format='DD/MM/YYYY'
                sx={{ color: 'text.primary' }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="End Date"
                minDate={filters.startDate ? moment(filters.startDate) : undefined}
                value={filters.endDate ? moment(filters.endDate) : null}
                onChange={(date) => handleFilterChange('endDate', date ? date.format('YYYY-MM-DD') : '')}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                format='DD/MM/YYYY'
                sx={{ color: 'text.primary' }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>User</InputLabel>
              <Select
                value={filters.userId || ''}
                label="User"
                onChange={(e) => handleFilterChange('userId', e.target.value)}
              >
                <MenuItem value="">
                  All Users
                </MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.firstName} {user.surname} ({user.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Action Type</InputLabel>
              <Select
                value={filters.actionDescription}
                label="Action Type"
                onChange={(e) => handleFilterChange('actionDescription', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="CREATED">Created</MenuItem>
                <MenuItem value="UPDATED">Updated</MenuItem>
                <MenuItem value="DELETED">Deleted</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {filters.actionDescription === 'UPDATED' &&
            <>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="From"
                  value={filters.oldValue}
                  onChange={(e) => handleFilterChange('oldValue', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="To"
                  value={filters.newValue}
                  onChange={(e) => handleFilterChange('newValue', e.target.value)}
                  size="small"
                />
              </Grid>
            </>}
          {filters.actionDescription !== 'UPDATED' && (
            // Spacer row to keep action buttons fixed below two rows
            <Grid item xs={12}>
              <Box sx={{ height: 40 }} />
            </Grid>
          )}
          {/* <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Model Name"
                  value={filters.referenceModelName}
                  onChange={(e) => handleFilterChange('referenceModelName', e.target.value)}
                  size="small"
                />
              </Grid> */}
          {/* <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Object ID"
                  value={filters.referenceObjectId}
                  onChange={(e) => handleFilterChange('referenceObjectId', e.target.value)}
                  size="small"
                />
              </Grid> */}
        </Grid>
      </Stack>
      <LoadingPage pageReady={!isLoading}>
        {userActionLogs && <TableDisplay
          headCells={headCells}
          rows={rows}
          haveCheckboxSelect={false}
          onOrderChange={handleOrderChange}
          isMobileMode={isMobileMode}
          selected={selected}
          setSelected={setSelected}
          selectByIcon={true}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage} />
        }
      </LoadingPage>
    </PageOuterContainer>
  );
};
